'use client';

import { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { X, Mail, Send, CheckCircle2, Loader2, Calendar, Gift, Download, ExternalLink } from 'lucide-react';
import { useExitIntent } from '@/hooks/useExitIntent';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import {
  getKBServiceMapping,
  getToolServiceMapping,
  getBlogServiceMapping,
  getDefaultServiceMapping,
  type ServiceMapping,
} from '@/lib/data/content-service-mapping';
// NOTE: This client component receives the resolved serviceMapping as a prop
// from server parents (blog/KB/tool pages) — that's the D1-backed path.
// The static-file lookup below is a fallback for callers that haven't been
// updated yet, so the popup never renders without a mapping.
import { resolveLeadMagnetForPage } from '@/lib/data/lead-magnets';
import { trackLeadFormSubmission, trackScheduleMeetingClick, trackExitIntent, trackFormStart } from '@/lib/analytics';
import { setLeadSubmittedCookie } from '@/lib/utils/lead-cookie';
import { containsNonLatinText, NON_ENGLISH_TEXT_ERROR } from '@/lib/text-validation';

interface ExitIntentPopupProps {
  /** Page type for contextual messaging */
  pageType: 'kb_article' | 'tool_page' | 'blog_post';
  /** Current page slug for tracking */
  pageSlug: string;
  /** Category for contextual mapping */
  category?: string;
  /** Custom title override */
  title?: string;
  /** Custom description override */
  description?: string;
  /** Disable the popup */
  disabled?: boolean;
  /** Pre-resolved service mapping (D1-backed). Server parents should pass
   *  this; falls back to static lookup if omitted. */
  serviceMapping?: ServiceMapping;
}

type FormStatus = 'idle' | 'submitting' | 'success' | 'error';

/**
 * Exit-intent popup that captures leaving visitors.
 * Shows contextual messaging based on page type and category.
 * Only triggers once per session.
 */
export default function ExitIntentPopup({
  pageType,
  pageSlug,
  category,
  title: titleOverride,
  description: descriptionOverride,
  disabled = false,
  serviceMapping: serviceMappingProp,
}: ExitIntentPopupProps) {
  const { showExitIntent, dismissExitIntent } = useExitIntent({ disabled });
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState<FormStatus>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const [formStarted, setFormStarted] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  useFocusTrap(modalRef, showExitIntent);

  // Get contextual messaging — prefer prop (D1-backed) over static-file lookup
  const getServiceMapping = (): ServiceMapping => {
    if (serviceMappingProp) return serviceMappingProp;

    let mapping: ServiceMapping | undefined;
    switch (pageType) {
      case 'kb_article':
        mapping = getKBServiceMapping(category || '', pageSlug);
        break;
      case 'tool_page':
        mapping = getToolServiceMapping(category || '', pageSlug);
        break;
      case 'blog_post':
        mapping = getBlogServiceMapping(category || '', pageSlug);
        break;
    }

    return mapping || getDefaultServiceMapping();
  };

  const serviceMapping = getServiceMapping();

  // Resolve a lead magnet offer for this page, if any. When present, the popup
  // transforms from "vague subscribe" to a concrete "get this free download"
  // value exchange (much higher conversion).
  const leadMagnet = resolveLeadMagnetForPage(pageType, category, pageSlug);

  // Exit intent titles based on page type
  const getContextualTitle = (): string => {
    if (titleOverride) return titleOverride;
    if (leadMagnet) return leadMagnet.config.headline;

    switch (pageType) {
      case 'kb_article':
        return 'Found this guide helpful?';
      case 'tool_page':
        return 'Need professional assistance?';
      case 'blog_post':
        return 'Want more insights like this?';
      default:
        return 'Before you go...';
    }
  };

  const getContextualDescription = (): string => {
    if (descriptionOverride) return descriptionOverride;
    if (leadMagnet) return leadMagnet.config.subtext;

    switch (pageType) {
      case 'kb_article':
        return `Get expert help with ${serviceMapping.primaryServiceName.toLowerCase()} and receive security tips directly to your inbox.`;
      case 'tool_page':
        return `Our team can help you with ${serviceMapping.primaryServiceName.toLowerCase()}. Drop your email and we'll reach out.`;
      case 'blog_post':
        return `Subscribe to get the latest security insights and learn about our ${serviceMapping.primaryServiceName.toLowerCase()} services.`;
      default:
        return serviceMapping.description;
    }
  };

  // Track and focus input when popup opens
  useEffect(() => {
    if (showExitIntent) {
      // Track that exit intent was shown
      trackExitIntent('shown', pageType);

      if (inputRef.current) {
        const timer = setTimeout(() => {
          inputRef.current?.focus();
        }, 100);
        return () => clearTimeout(timer);
      }
    }
  }, [showExitIntent, pageType]);

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showExitIntent) {
        trackExitIntent('dismissed_x', pageType); // Escape is like X button
        dismissExitIntent();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showExitIntent, dismissExitIntent, pageType]);

  // Prevent body scroll when popup is open
  useEffect(() => {
    if (showExitIntent) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [showExitIntent]);

  const validateEmail = (email: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  // Track form start on first field focus
  const handleFormFocus = () => {
    if (!formStarted) {
      setFormStarted(true);
      trackFormStart('Exit Intent', pageType);
      trackExitIntent('engaged', pageType);
    }
  };

  // Track dismiss via X button
  const handleDismissX = () => {
    trackExitIntent('dismissed_x', pageType);
    dismissExitIntent();
  };

  // Track dismiss via backdrop click
  const handleDismissBackdrop = () => {
    trackExitIntent('dismissed_backdrop', pageType);
    dismissExitIntent();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Honeypot check
    if (honeypot) {
      setStatus('success');
      return;
    }

    const trimmedEmail = email.trim();
    const trimmedName = name.trim();
    const trimmedMessage = message.trim();

    if (!trimmedName) {
      setErrorMessage('Please enter your name');
      return;
    }

    if (!trimmedEmail) {
      setErrorMessage('Please enter your email address');
      return;
    }

    if (!validateEmail(trimmedEmail)) {
      setErrorMessage('Please enter a valid email address');
      return;
    }

    if (!trimmedMessage) {
      setErrorMessage('Please tell us how we can help');
      return;
    }

    // Block non-English (non-Latin script) input in free-text fields
    if (containsNonLatinText(trimmedName) || containsNonLatinText(trimmedMessage)) {
      setErrorMessage(NON_ENGLISH_TEXT_ERROR);
      return;
    }

    setStatus('submitting');
    setErrorMessage('');

    try {
      const nameParts = trimmedName.split(/\s+/);
      const firstName = nameParts[0] || '';
      const lastName = nameParts.length > 1 ? nameParts.slice(1).join(' ') : '';

      // Build message: user message is required, plus source info appended.
      const magnetNote = leadMagnet ? ` (magnet: ${leadMagnet.config.asset.name})` : '';
      const fullMessage = `${trimmedMessage}\n\n---\nSource: ${pageType} - ${pageSlug}${magnetNote}`;

      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName,
          lastName,
          email: trimmedEmail,
          message: fullMessage,
          formName: leadMagnet
            ? `Exit Intent - ${leadMagnet.config.asset.name}`
            : 'Exit Intent - Email Capture',
          src: 'exit-intent',
          id: pageSlug,
          cat: category,
          magnetKey: leadMagnet?.magnetKey,
          pageUrl: typeof window !== 'undefined' ? window.location.href : undefined,
        }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to submit');
      }

      // Track conversion in GA4 and Clarity (includes identification)
      trackLeadFormSubmission('Exit Intent - Email Capture', pageType, undefined, trimmedEmail);
      trackExitIntent('submitted', pageType);

      setStatus('success');

      // Set cookie to suppress exit intent for 7 days
      setLeadSubmittedCookie();
    } catch {
      setStatus('error');
      setErrorMessage('Something went wrong. Please try again.');
    }
  };

  if (!showExitIntent) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm"
        onClick={handleDismissBackdrop}
        aria-hidden="true"
      />

      {/* Modal */}
      <div
        ref={modalRef}
        className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md animate-in fade-in zoom-in-95 duration-200 overflow-hidden"
        role="dialog"
        aria-modal="true"
        aria-labelledby="exit-intent-title"
      >
        {/* Close button */}
        <button
          onClick={handleDismissX}
          className="absolute top-3 right-3 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        {status === 'success' ? (
          /* Success State */
          <div className="p-8 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 dark:bg-green-900/40 rounded-full mb-4">
              <CheckCircle2 className="w-8 h-8 text-green-600 dark:text-green-400" />
            </div>
            {leadMagnet ? (
              <>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">You&apos;re all set!</h2>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  Here&apos;s your download. Bookmark it — we update it regularly.
                </p>
                <div className="space-y-3">
                  <a
                    href={leadMagnet.config.asset.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 w-full py-3 px-4 bg-[#2813e8] hover:bg-[#1f0fb8] text-white font-semibold rounded-lg transition-colors"
                  >
                    <ExternalLink className="w-5 h-5" />
                    {leadMagnet.config.asset.linkLabel || `Get the ${leadMagnet.config.asset.name}`}
                  </a>
                  <button
                    onClick={dismissExitIntent}
                    className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 text-sm transition-colors"
                  >
                    Continue browsing
                  </button>
                </div>
              </>
            ) : (
              <>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">You&apos;re In!</h2>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  We&apos;ll be in touch soon with helpful insights and information about our services.
                </p>
                <div className="space-y-3">
                  <a
                    href="https://hs.inventivehq.com/meetings/sean1324/contactpage"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackScheduleMeetingClick('exit_intent_success')}
                    className="inline-flex items-center justify-center gap-2 w-full py-3 px-4 bg-[#2813e8] hover:bg-[#1f0fb8] text-white font-medium rounded-lg transition-colors"
                  >
                    <Calendar className="w-5 h-5" />
                    Schedule a Call Now
                  </a>
                  <button
                    onClick={dismissExitIntent}
                    className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 text-sm transition-colors"
                  >
                    Continue browsing
                  </button>
                </div>
              </>
            )}
          </div>
        ) : (
          <>
            {/* Header with gradient */}
            <div className="bg-gradient-to-r from-[#2813e8] to-[#2cc3d1] text-white p-6 pb-8">
              {leadMagnet && (
                <div className="inline-flex items-center gap-2 bg-white/20 dark:bg-gray-800/20 rounded-full px-3 py-1 text-xs font-medium mb-3">
                  <Download className="w-3.5 h-3.5" />
                  Free Download
                </div>
              )}
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-white/20 dark:bg-gray-800/20 rounded-full">
                  {leadMagnet ? <Download className="w-6 h-6" /> : <Gift className="w-6 h-6" />}
                </div>
                <h2 id="exit-intent-title" className="text-xl font-bold">
                  {getContextualTitle()}
                </h2>
              </div>
              <p className="text-white/90 text-sm leading-relaxed">
                {getContextualDescription()}
              </p>
              {leadMagnet && (
                <p className="text-white/70 text-xs mt-2">
                  <strong>{leadMagnet.config.asset.name}</strong> — {leadMagnet.config.asset.description}
                </p>
              )}
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} name="Exit Intent - Email Capture" className="p-6 -mt-4">
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-4 space-y-4">
                {/* Honeypot */}
                <div className="absolute -left-[9999px]" aria-hidden="true">
                  <input
                    type="text"
                    name="website"
                    value={honeypot}
                    onChange={(e) => setHoneypot(e.target.value)}
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    aria-label="Leave blank"
                  />
                </div>

                {/* Name (required) */}
                <div>
                  <input
                    ref={inputRef}
                    type="text"
                    aria-label="Your name"
                    value={name}
                    onChange={(e) => {
                      setName(e.target.value);
                      // Warn immediately on non-English (non-Latin script) input
                      if (containsNonLatinText(e.target.value)) {
                        setErrorMessage(NON_ENGLISH_TEXT_ERROR);
                      } else if (errorMessage) {
                        setErrorMessage('');
                      }
                    }}
                    onFocus={handleFormFocus}
                    placeholder="Your name *"
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-[#2813e8] focus:border-transparent transition-colors"
                    autoComplete="name"
                    disabled={status === 'submitting'}
                  />
                </div>

                {/* Email */}
                <div>
                  <label htmlFor="exit-email" className="sr-only">
                    Email address
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Mail className="w-5 h-5 text-gray-400" />
                    </div>
                    <input
                      id="exit-email"
                      type="email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        if (errorMessage) setErrorMessage('');
                      }}
                      onFocus={handleFormFocus}
                      placeholder="Email address *"
                      className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#2813e8] focus:border-transparent transition-colors ${
                        errorMessage ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                      }`}
                      autoComplete="email"
                      disabled={status === 'submitting'}
                    />
                  </div>
                </div>

                {/* Message (required) */}
                <div>
                  <textarea
                    aria-label="How can we help?"
                    value={message}
                    onChange={(e) => {
                      setMessage(e.target.value);
                      // Warn immediately on non-English (non-Latin script) input
                      if (containsNonLatinText(e.target.value)) {
                        setErrorMessage(NON_ENGLISH_TEXT_ERROR);
                      } else if (errorMessage) {
                        setErrorMessage('');
                      }
                    }}
                    onFocus={handleFormFocus}
                    placeholder="How can we help? *"
                    rows={3}
                    required
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-[#2813e8] focus:border-transparent transition-colors resize-none"
                    disabled={status === 'submitting'}
                  />
                </div>

                {/* Error message */}
                {errorMessage && (
                  <p className="text-sm text-red-600 dark:text-red-400" role="alert">
                    {errorMessage}
                  </p>
                )}

                {/* Submit */}
                <button
                  type="submit"
                  disabled={status === 'submitting'}
                  className="w-full py-3 px-4 bg-[#2813e8] hover:bg-[#1f0fb8] text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {status === 'submitting' ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      {leadMagnet ? 'Sending...' : 'Subscribing...'}
                    </>
                  ) : leadMagnet ? (
                    <>
                      <Download className="w-5 h-5" />
                      Get Free Download
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      Get Expert Help
                    </>
                  )}
                </button>
              </div>

              <p className="mt-4 text-xs text-gray-500 dark:text-gray-400 text-center">
                No spam, ever. Unsubscribe anytime.{' '}
                <Link href="/privacy-policy" className="text-[#2813e8] hover:underline">
                  Privacy Policy
                </Link>
              </p>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
